import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useParams } from 'react-router-dom'
import axios from 'axios'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog'
import { Loader2, Heart, Copy, Share2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  translations,
  formatDate,
  formatDateRange,
  formatTimeRange,
} from '../lib/translations'
import { getApiUrl, resolveImageUrl, currencySymbol, formatMoney } from '../lib/utils'
import { wishlistEmoji } from '../lib/wishlistIcons'
import { pollPaymentStatus } from '../lib/payments'
import { usePaymentOverlay } from '../contexts/PaymentOverlayContext'
import {
  EVENT_TZ_OFFSET,
  countdownLabels,
  eventStartMs,
  useCountdown,
} from '../lib/countdown'
import {
  buildTemplateLook,
  cleanTemplateId,
  getTemplate,
  loadTemplateFonts,
  sampleEventFor,
} from '../lib/inviteTemplates'
import { buildInviteLook, loadInviteFont } from '../lib/inviteStyle'
import { PARTICLES, getMotif } from '../components/invite/motifs'
import TemplateMotifLayer from '../components/invite/TemplateMotifLayer'
import WaxSeal from '../components/invite/WaxSeal'
import PaintedCard from '../components/invite/PaintedCard'
import { paintedPageOverrides, resolvePaintedChoice } from '../lib/paintedInvite'
import waxSeal from '../assets/wax-seal.webp'
import blueWaxSeal from '../assets/blue-wax-seal.webp'
import goldWaxSeal from '../assets/gold-wax-seal.webp'
import redWaxSeal from '../assets/red-wax-seal.webp'
import navyWaxSeal from '../assets/navy-wax-seal.webp'
import palePinkWaxSeal from '../assets/pale-pink-wax-seal.webp'
import './InvitationPage.css'

const API = getApiUrl()

// Events are scheduled in Georgia's timezone (Asia/Tbilisi is a fixed UTC+4
// with no daylight saving), used to anchor calendar exports correctly.
// EVENT_TZ_OFFSET lives in lib/countdown so the dashboard cards share it.
const EVENT_TZID = 'Asia/Tbilisi'

// ---------------------------------------------------------------------------
// Fixed decorative layers (corner motifs, twinkling stars, falling petals).
// Two looks share one layout: the botanical default and a geometric
// navy-and-gold "corporate" variant (thin diagonal corner lines + diamonds).
// ---------------------------------------------------------------------------

const CornerMotif = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 120 120"
    fill="none"
    stroke="#7fa895"
    strokeWidth="1.2"
    opacity=".55"
  >
    <path d="M76 44 a26 26 0 1 0 8 34 a20 20 0 1 1 -8 -34z" fill="#d8a48f" fillOpacity=".2" />
    <path d="M30 24 l3 8 8 3 -8 3 -3 8 -3 -8 -8 -3 8 -3z" />
    <path d="M92 84 l2.4 6 6 2.4 -6 2.4 -2.4 6 -2.4 -6 -6 -2.4 6 -2.4z" fill="#d8a48f" stroke="none" />
    <circle cx="56" cy="16" r="2.5" fill="#d8a48f" stroke="none" />
    <circle cx="16" cy="76" r="2" fill="#7fa895" stroke="none" />
  </svg>
)

// Corporate corners: three fading diagonal lines crossing the corner, a gold
// dot, a diamond outline and a small navy dot.
const CorpCornerMotif = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 120 120"
    fill="none"
    stroke="#3d5a75"
    strokeWidth="1.2"
    opacity=".55"
  >
    <path d="M0 118 L118 0" />
    <path d="M0 92 L92 0" strokeOpacity=".55" />
    <path d="M0 66 L66 0" strokeOpacity=".35" />
    <circle cx="88" cy="30" r="4.5" fill="#b3873e" stroke="none" />
    <rect x="24" y="70" width="9" height="9" transform="rotate(45 28.5 74.5)" />
    <circle cx="52" cy="52" r="2.6" fill="#3d5a75" stroke="none" />
  </svg>
)

// Birthday corners: a blush branch sweeping across the corner with leaves on
// both sides, a terracotta bud at the tip and soft blossom dots.
const BdayCornerMotif = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 120 120"
    fill="none"
    stroke="#b98a92"
    strokeWidth="1.2"
    opacity=".55"
  >
    <path d="M8 112 C 20 70, 40 40, 100 12" />
    <path d="M26 84 q -14 -4 -18 -16 q 14 -2 18 16z" />
    <path d="M42 62 q -14 -4 -18 -16 q 14 -2 18 16z" />
    <path d="M62 42 q -14 -4 -18 -16 q 14 -2 18 16z" />
    <path d="M34 92 q 4 -14 16 -18 q 2 14 -16 18z" />
    <path d="M52 70 q 4 -14 16 -18 q 2 14 -16 18z" />
    <path d="M74 50 q 4 -14 16 -18 q 2 14 -16 18z" />
    <circle cx="97" cy="17" r="3.4" fill="#b0755b" stroke="none" />
    <circle cx="46" cy="52" r="4.6" fill="#d9a3a3" fillOpacity=".55" stroke="none" />
    <circle cx="68" cy="34" r="3.6" fill="#d9a3a3" fillOpacity=".55" stroke="none" />
  </svg>
)

// Heart in a 20x20 box, shared by the wedding corner motif and the wedding
// falling shapes.
const HEART_PATH =
  'M10 18 C 4.5 13, 1.5 9.4, 1.5 6.4 C 1.5 3.4, 3.9 1.8, 6.2 1.8 C 7.9 1.8, 9.3 2.7, 10 4.1 C 10.7 2.7, 12.1 1.8, 13.8 1.8 C 16.1 1.8, 18.5 3.4, 18.5 6.4 C 18.5 9.4, 15.5 13, 10 18 Z'

// Wedding corners: a vein of blooming hearts sweeping across the corner —
// largest at the base, a filled bud near the tip — ending in two interlocked
// gold rings crowned with a tiny diamond, plus heart confetti.
const WeddingCornerMotif = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 120 120"
    fill="none"
    stroke="#b98a8f"
    strokeWidth="1.2"
    opacity=".55"
  >
    <path d="M8 112 C 24 76, 44 44, 88 26" />
    <path d={HEART_PATH} transform="translate(14 76) scale(1.1)" fill="#d9a3a3" fillOpacity=".25" />
    <path d={HEART_PATH} transform="translate(40 55) scale(.8)" fill="#d9a3a3" fillOpacity=".25" />
    <path d={HEART_PATH} transform="translate(62 37) scale(.55)" fill="#bb6b7c" fillOpacity=".5" stroke="none" />
    <circle cx="94" cy="24" r="6.5" stroke="#c49a5f" />
    <circle cx="103" cy="18" r="6.5" stroke="#c49a5f" />
    <path d="M98.5 2.5 l3.2 3.8 -3.2 3.8 -3.2 -3.8z" fill="#c49a5f" stroke="none" />
    <path d={HEART_PATH} transform="translate(22 98) scale(.3)" fill="#bb6b7c" fillOpacity=".6" stroke="none" />
    <path d={HEART_PATH} transform="translate(52 18) scale(.28)" fill="#d9a3a3" fillOpacity=".7" stroke="none" />
  </svg>
)

// Graduation corners: a laurel branch sweeping across the corner with leaf
// pairs, ending in a mortarboard with a gold tassel, plus gold star confetti.
const GradCornerMotif = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 120 120"
    fill="none"
    stroke="#8474ad"
    strokeWidth="1.2"
    opacity=".55"
  >
    <path d="M8 112 C 22 74, 44 44, 86 20" />
    <path d="M28 84 q -14 -4 -18 -16 q 14 -2 18 16z" />
    <path d="M36 92 q 4 -14 16 -18 q 2 14 -16 18z" />
    <path d="M46 62 q -14 -4 -18 -16 q 14 -2 18 16z" />
    <path d="M56 70 q 4 -14 16 -18 q 2 14 -16 18z" />
    <path d="M68 44 q -14 -4 -18 -16 q 14 -2 18 16z" />
    <path d="M78 52 q 4 -14 16 -18 q 2 14 -16 18z" />
    <path d="M100 8 L116 16 L100 24 L84 16z" fill="#8474ad" fillOpacity=".25" />
    <path d="M93 20.5 v4.5 c0 2.5 3.1 4.5 7 4.5 s7 -2 7 -4.5 v-4.5" />
    <path d="M116 16 v8.5" stroke="#c1913b" />
    <circle cx="116" cy="27" r="2.2" fill="#c1913b" stroke="none" />
    <path d="M30 28 l2.4 6 6 2.4 -6 2.4 -2.4 6 -2.4 -6 -6 -2.4 6 -2.4z" fill="#c1913b" stroke="none" />
    <circle cx="58" cy="18" r="2.2" fill="#c1913b" stroke="none" />
  </svg>
)

// Baby-shower corners: a mobile arc sweeping across the corner with stars
// dangling from threads, a crescent moon at the tip and drifting cloud puffs.
const BabyCornerMotif = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 120 120"
    fill="none"
    stroke="#7fa3bd"
    strokeWidth="1.2"
    opacity=".55"
  >
    <path d="M8 112 C 24 76, 46 46, 90 22" />
    <path d="M30 82 v10" strokeOpacity=".7" />
    <path d="M30 96 l1.7 4.2 4.2 1.7 -4.2 1.7 -1.7 4.2 -1.7 -4.2 -4.2 -1.7 4.2 -1.7z" fill="#d48ba6" stroke="none" />
    <path d="M52 62 v9" strokeOpacity=".7" />
    <path d="M52 75 l1.5 3.6 3.6 1.5 -3.6 1.5 -1.5 3.6 -1.5 -3.6 -3.6 -1.5 3.6 -1.5z" fill="#7fa3bd" stroke="none" />
    <path d="M74 44 v8" strokeOpacity=".7" />
    <path d="M74 56 l1.3 3.2 3.2 1.3 -3.2 1.3 -1.3 3.2 -1.3 -3.2 -3.2 -1.3 3.2 -1.3z" fill="#d48ba6" stroke="none" />
    <path d="M104 10 a11 11 0 1 0 6 19 a8.5 8.5 0 1 1 -6 -19z" fill="#7fa3bd" fillOpacity=".22" />
    <path d="M18 38 q 3 -6 9 -5 q 2 -5 8 -4 q 6 1 6 6 q 5 1 4 6 h -26 q -2 -1 -1 -3z" stroke="#d48ba6" strokeOpacity=".75" />
    <circle cx="58" cy="24" r="2.2" fill="#d48ba6" stroke="none" />
    <circle cx="16" cy="66" r="2" fill="#7fa3bd" stroke="none" />
  </svg>
)

const PETAL_COLORS = ['#d8a48f', '#7fa895', '#a5b89b']
const STAR_CHARS = ['✶', '✦', '·']
// Corporate: slow-falling gold/slate sparkles instead of petals, diamond
// twinkles instead of stars.
const CORP_SPARK_COLORS = ['#b3873e', '#8fa0b8', '#c9ab66']
const CORP_STAR_CHARS = ['✦', '◆', '·']
// Birthday: blush petals and tiny blossoms/hearts.
const BDAY_PETAL_COLORS = ['#e3b7ab', '#d493a2', '#e7cbb8']
const BDAY_STAR_CHARS = ['✿', '❀', '♥']
// Wedding: falling wine-rose hearts with heart twinkles.
const WED_PETAL_COLORS = ['#bb6b7c', '#d9a3a3', '#c49a5f']
const WED_STAR_CHARS = ['♥', '♡', '·']
// Graduation: falling purple/gold mortarboards with star twinkles.
const GRAD_PETAL_COLORS = ['#8474ad', '#c1913b', '#a99ac6']
const GRAD_STAR_CHARS = ['★', '✦', '·']
// Baby shower: falling baby-pink and baby-blue stars with moon twinkles —
// strictly alternating pink/blue (3:3) for an even split.
const BABY_PETAL_COLORS = ['#e0a4bd', '#8fb2cc', '#eec3d4', '#a8c3d8', '#d48ba6', '#7fa3bd']
const BABY_STAR_CHARS = ['☾', '✦', '·']

// Five-point star in a 20x20 box — the baby-shower "petal".
const STAR5_PATH =
  'M10 1 L12.6 7.2 L19.4 7.7 L14.2 12.1 L15.8 18.8 L10 15.2 L4.2 18.8 L5.8 12.1 L0.6 7.7 L7.4 7.2 Z'

// Tossed mortarboard in a 20x20 box: diamond board, crown below, gold-side
// tassel — the graduation "petal".
const CAP_PATH =
  'M10 2.5 L19 7 L10 11.5 L1 7 Z M5.6 9.3 V12.7 C5.6 14.5 7.5 15.8 10 15.8 C12.5 15.8 14.4 14.5 14.4 12.7 V9.3 L10 11.4 Z M18 7.6 H19 V12.2 H18 Z M17.4 12.2 H19.6 L19.9 15.6 H17.1 Z'

// 4-point diamond sparkle used as the corporate "petal".
const SPARK_PATH = 'M10 0 L12.4 7.6 L20 10 L12.4 12.4 L10 20 L7.6 12.4 L0 10 L7.6 7.6 Z'
const PETAL_PATH = 'M10 1 C 16 5, 16 13, 10 19 C 4 13, 4 5, 10 1z'

// ---------------------------------------------------------------------------
// Designed templates (lib/inviteTemplates.ts). Instead of a four-corner SVG
// motif these hang the design's own cut-out artwork off the edges of the
// viewport — the same pieces, in the same places, as the standalone designs in
// invites/ and design-mockups/. The files are served from public/templates/art
// rather than bundled: they are a couple of megabytes of PNG that only the one
// chosen template ever needs.
// ---------------------------------------------------------------------------
const TPL_ART = '/templates/art'

// The card corners inside a designed template: a plain sprig that takes its
// colour from the template's own accent rather than a hardcoded palette, so
// one motif serves all five.
const TplCornerMotif = ({ className }) => (
  <svg className={className} viewBox="0 0 120 120" fill="none" stroke="currentColor" strokeWidth="1.2" opacity=".5">
    <path d="M10 110 C 24 74, 46 44, 96 16" />
    <path d="M30 82 q -13 -4 -17 -15 q 13 -2 17 15z" />
    <path d="M50 60 q -13 -4 -17 -15 q 13 -2 17 15z" />
    <path d="M38 90 q 4 -13 15 -17 q 2 13 -15 17z" />
    <path d="M58 68 q 4 -13 15 -17 q 2 13 -15 17z" />
    <circle cx="94" cy="20" r="3.2" fill="currentColor" stroke="none" />
  </svg>
)

// The six seal photographs by colour — a data-driven template names one of
// these (InviteTemplate.seal).
const SEALS_BY_COLOR = {
  red: redWaxSeal,
  gold: goldWaxSeal,
  blue: blueWaxSeal,
  navy: navyWaxSeal,
  pink: palePinkWaxSeal,
  sage: waxSeal,
}

// Wax seal per look. Templates reuse the same six seal photos as the
// per-type variants — each design was given the one that matches its ink.
const TEMPLATE_SEALS = {
  corporate: blueWaxSeal,
  birthday: goldWaxSeal,
  wedding: redWaxSeal,
  graduation: navyWaxSeal,
  babyshower: palePinkWaxSeal,
  'blue-script': blueWaxSeal,
  'rainbow-kids': palePinkWaxSeal,
  'retro-festive': redWaxSeal,
  'red-bows': redWaxSeal,
  cocktail: goldWaxSeal,
}

// Each piece is [class, file]; positioning lives in InvitationPage.css next to
// the template's palette, so the artwork and its colours stay together.
const TEMPLATE_ART = {
  'blue-script': [
    ['ribbon-l', 'design1-ribbon-left.png'],
    ['ribbon-r', 'design1-ribbon-right.png'],
    ['cake', 'design1-cake.png'],
  ],
  'rainbow-kids': [
    ['bunt-l', 'design2-bunting-left.png'],
    ['bunt-r', 'design2-bunting-right.png'],
    ['rainbow', 'design2-rainbow.png'],
    ['gift-l', 'design2-gift-left.png'],
    ['gift-r', 'design2-gift-right.png'],
  ],
  'retro-festive': [
    ['bunt-l', 'design3-bunting-left.png'],
    ['bunt-r', 'design3-bunting-right.png'],
    ['balloon-l', 'design3-balloon-left.png'],
    ['balloon-r', 'design3-balloon-right.png'],
    ['bot-l', 'design3-bottom-left.png'],
    ['bot-r', 'design3-bottom-right.png'],
    ['cake', 'design3-cake.png'],
  ],
  'red-bows': [
    ['tower', 'tower.png'],
    ['cake', 'cake.png'],
  ],
  cocktail: [],
}

// Red Bows draws its signature decoration as line art, not cut-out PNGs: three
// bows across the top, the ribbon swagged between them and a streamer curling
// down each side. Ported from design-mockups/invite-red-bows.html.
const RedBowsDecor = () => {
  const swag = (
    <>
      <path d="M 0 4 C 44 30, 84 58, 132 70" strokeWidth="4.2" vectorEffect="non-scaling-stroke" />
      <path d="M 132 70 C 178 82, 222 82, 268 70" strokeWidth="5.4" vectorEffect="non-scaling-stroke" />
      <path d="M 268 70 C 316 58, 356 30, 400 4" strokeWidth="4.2" vectorEffect="non-scaling-stroke" />
    </>
  )
  const streamer = (
    <>
      <path d="M 186 6 C 160 44, 112 74, 108 122" strokeWidth="5.2" />
      <path d="M 108 122 C 104 172, 146 200, 142 250" strokeWidth="4.6" />
      <path d="M 142 250 C 138 300, 86 328, 82 378" strokeWidth="4" />
      <path d="M 82 378 C 78 424, 116 452, 106 500" strokeWidth="3.4" />
      <path d="M 106 500 C 100 534, 112 560, 102 588" strokeWidth="2.8" />
      <path d="M 102 588 C 96 610, 72 614, 66 601 C 61 590, 74 581, 81 590" strokeWidth="2.4" />
      <circle cx="72" cy="589" r="3.4" className="solid" />
    </>
  )
  return (
    <>
      <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
        <defs>
          <g id="invBow">
            <path d="M -7 -1 C -22 -16, -38 -25, -52 -26" strokeWidth="3.2" />
            <path d="M -52 -26 C -67 -27, -75 -20, -70 -11 C -66 -4, -56 -1, -44 1" strokeWidth="5.2" />
            <path d="M -44 1 C -31 3, -16 4, -7 3" strokeWidth="3.2" />
            <path d="M 7 -1 C 22 -16, 38 -25, 52 -26" strokeWidth="3.2" />
            <path d="M 52 -26 C 67 -27, 75 -20, 70 -11 C 66 -4, 56 -1, 44 1" strokeWidth="5.2" />
            <path d="M 44 1 C 31 3, 16 4, 7 3" strokeWidth="3.2" />
            <path
              d="M -8 -3 C -3 -7, 3 -7, 8 -3 C 10 0, 10 3, 8 6 C 3 9, -3 9, -8 6 C -10 3, -10 0, -8 -3 Z"
              fill="var(--inv-ink)"
              stroke="none"
            />
          </g>
        </defs>
      </svg>
      <svg className="d d-swag d-swag-l" viewBox="0 0 400 120" preserveAspectRatio="none" aria-hidden="true">{swag}</svg>
      <svg className="d d-swag d-swag-r" viewBox="0 0 400 120" preserveAspectRatio="none" aria-hidden="true">{swag}</svg>
      <svg className="d d-str d-str-l" viewBox="0 0 190 620" aria-hidden="true">
        <g className="str-path">{streamer}</g>
      </svg>
      <svg className="d d-str d-str-r" viewBox="0 0 190 620" aria-hidden="true">
        <g className="str-path" transform="translate(190 0) scale(-1 1)">{streamer}</g>
      </svg>
      <svg className="d d-bow d-bow-l" viewBox="0 0 170 80" aria-hidden="true">
        <use href="#invBow" transform="translate(85 38) rotate(-6)" />
      </svg>
      <svg className="d d-bow d-bow-c" viewBox="0 0 170 100" aria-hidden="true">
        <path d="M 79 48 C 72 66, 78 82, 69 100" strokeWidth="2.8" />
        <path d="M 91 48 C 98 66, 92 82, 101 100" strokeWidth="2.8" />
        <use href="#invBow" transform="translate(85 38)" />
      </svg>
      <svg className="d d-bow d-bow-r" viewBox="0 0 170 80" aria-hidden="true">
        <use href="#invBow" transform="translate(85 38) rotate(6)" />
      </svg>
    </>
  )
}

const TEMPLATE_DECOR = { 'red-bows': RedBowsDecor }

// A template with a poster IS its photograph — the design is built around that
// one picture, so the frame on screen 1 always shows it rather than whatever
// the host uploaded. The host's cover photo moves to the RSVP card instead, so
// it still gets seen without displacing the design.
const TEMPLATE_POSTER = {
  cocktail: `${TPL_ART}/cocktail-poster.jpeg`,
}

const TemplateArt = ({ variant }) => {
  const pieces = TEMPLATE_ART[variant]
  const Decor = TEMPLATE_DECOR[variant]
  if (!pieces?.length && !Decor) return null
  return (
    <div className="inv-tart" aria-hidden="true">
      {Decor && <Decor />}
      {(pieces || []).map(([cls, file]) => (
        <img key={cls} className={`inv-tart-${cls}`} src={`${TPL_ART}/${file}`} alt="" />
      ))}
    </div>
  )
}

// Per-look decoration kit: corner motif, falling-shape path + colors, and the
// twinkle characters. The invitation picks a look from the chosen template,
// falling back to the event type when the host left it on "auto".
// Template entries carry no Corner: their artwork replaces the corner motifs.
const DECOR_VARIANTS = {
  default: { Corner: CornerMotif, colors: PETAL_COLORS, chars: STAR_CHARS, path: PETAL_PATH, foot: '✶ ✶ ✶' },
  corporate: { Corner: CorpCornerMotif, colors: CORP_SPARK_COLORS, chars: CORP_STAR_CHARS, path: SPARK_PATH, foot: '✦ ✦ ✦' },
  birthday: { Corner: BdayCornerMotif, colors: BDAY_PETAL_COLORS, chars: BDAY_STAR_CHARS, path: PETAL_PATH, foot: '♥ ♥ ♥' },
  wedding: { Corner: WeddingCornerMotif, colors: WED_PETAL_COLORS, chars: WED_STAR_CHARS, path: HEART_PATH, foot: '♡ ♥ ♡' },
  graduation: { Corner: GradCornerMotif, colors: GRAD_PETAL_COLORS, chars: GRAD_STAR_CHARS, path: CAP_PATH, foot: '★ ✦ ★' },
  babyshower: { Corner: BabyCornerMotif, colors: BABY_PETAL_COLORS, chars: BABY_STAR_CHARS, path: STAR5_PATH, foot: '☾ ✦ ☾' },
  'blue-script': { colors: ['#2f45c0', '#5c70d6', '#8fa0e8'], chars: ['♥', '♡', '❀'], path: PETAL_PATH, foot: '♡ ♥ ♡' },
  'rainbow-kids': { colors: ['#ef7fae', '#b39ddb', '#8fb8e8', '#f5c957', '#93c98b'], chars: ['★', '✦', '✧'], path: STAR5_PATH, foot: '★ ✦ ★' },
  'retro-festive': { colors: ['#3d55a5', '#d94f3d', '#e8846f'], chars: ['▮', '●', '▲', '✶'], path: SPARK_PATH, foot: '✶ ● ✶' },
  'red-bows': { colors: ['#ac0000', '#c94a4a', '#e0b0a0'], chars: ['♥', '♡', '❦'], path: HEART_PATH, foot: '♡ ♥ ♡' },
  cocktail: { colors: ['#a08a5b', '#8a8880', '#c9c6bd'], chars: ['✦', '·', '✧'], path: SPARK_PATH, foot: '✦ · ✦' },
}

// A data-driven template's kit, built from its motif (falling shape, twinkle
// characters, footer) and its palette (the colours the shapes fall in).
const motifKitFor = (template) => {
  const motif = getMotif(template.motif)
  const p = template.palette
  return {
    Corner: null,
    colors: [p.a1, p.a2, p.gold],
    chars: motif.chars,
    path: PARTICLES[motif.fall] || PETAL_PATH,
    foot: motif.foot,
  }
}

// The card-corner motif of a data-driven template: its own icon, faded.
const motifCornerFor = (template) => {
  const { Icon } = getMotif(template.motif)
  const MotifCorner = ({ className }) => (
    <svg className={className} viewBox="0 0 100 100" aria-hidden="true">
      <Icon />
    </svg>
  )
  return MotifCorner
}

// Which kit dresses this variant: a hand-written one for the per-type looks
// and the five original designs, the motif-derived one for everything else.
const kitFor = (variant, template) =>
  DECOR_VARIANTS[variant] || (template && !template.legacy ? motifKitFor(template) : DECOR_VARIANTS.default)

const DecorLayer = ({ variant = 'default', template = null }) => {
  const { Corner, colors, chars, path } = kitFor(variant, template)
  const dataDriven = !!template && !template.legacy && template.id !== 'auto'
  // Random positions generated once per mount, stable across re-renders.
  const items = useMemo(() => {
    const petals = Array.from({ length: 22 }, (_, i) => ({
      left: Math.random() * 100,
      size: 9 + Math.random() * 10,
      drift: Math.round(Math.random() * 160 - 80),
      dur: 10 + Math.random() * 10,
      del: Math.random() * 12,
      spin: 2.5 + Math.random() * 2,
      color: colors[i % colors.length],
    }))
    const stars = Array.from({ length: 22 }, (_, i) => ({
      left: Math.random() * 100,
      top: Math.random() * 100,
      size: 8 + Math.random() * 12,
      dur: 2.4 + Math.random() * 4,
      del: Math.random() * 5,
      dx: Math.round(Math.random() * 44 - 22),
      dy: Math.round(Math.random() * 44 - 22),
      // Baby shower splits twinkles evenly blue/pink; other looks stay 2:1.
      warm: variant === 'babyshower' ? i % 2 === 0 : i % 3 === 0,
      char: chars[i % chars.length],
    }))
    return { petals, stars }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <TemplateArt variant={variant} />
      {dataDriven && (
        <TemplateMotifLayer motif={template.motif} frame={template.frame} layout={template.layout} />
      )}
      {Corner && (
        <>
          <Corner className="inv-bot tl" />
          <Corner className="inv-bot tr" />
          <Corner className="inv-bot bl" />
          <Corner className="inv-bot br" />
        </>
      )}
      {items.petals.map((p, i) => (
        <div
          key={`p${i}`}
          className="inv-petal"
          style={{
            left: `${p.left}%`,
            '--dur': `${p.dur}s`,
            '--del': `${p.del}s`,
            '--drift': `${p.drift}px`,
          } as any}
        >
          <svg width={p.size} height={p.size} viewBox="0 0 20 20" style={{ '--spin': `${p.spin}s` } as any}>
            <path d={path} fill={p.color} opacity={path === SPARK_PATH ? '.55' : '.7'} />
          </svg>
        </div>
      ))}
      {items.stars.map((s, i) => (
        <div
          key={`s${i}`}
          className={`inv-twinkle${s.warm ? ' warm' : ''}`}
          style={{
            left: `${s.left}%`,
            top: `${s.top}%`,
            fontSize: `${s.size}px`,
            '--dur': `${s.dur}s`,
            '--del': `${s.del}s`,
            '--dx': `${s.dx}px`,
            '--dy': `${s.dy}px`,
          } as any}
        >
          {s.char}
        </div>
      ))}
    </>
  )
}

// ---------------------------------------------------------------------------
// Envelope intro — the invitation first loads sealed inside an envelope; the
// guest taps the wax seal, the flap lifts, the letter slides out and the
// overlay dissolves into the full invite. Skipped inside iframes (host-edit /
// live preview), on payment-redirect returns (?gift=) and with ?noIntro=1.
// ---------------------------------------------------------------------------
const ENVELOPE_OPEN_MS = 1700
const ENVELOPE_OPEN_REDUCED_MS = 300

const EnvelopeIntro = ({ sealImg, seal, photo, kicker, title, dateLine, hint, onDone }) => {
  const [opening, setOpening] = useState(false)
  const reduced = useMemo(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    [],
  )
  const open = () => {
    if (opening) return
    setOpening(true)
    setTimeout(onDone, reduced ? ENVELOPE_OPEN_REDUCED_MS : ENVELOPE_OPEN_MS)
  }
  return (
    <div
      className={`inv-env-overlay${opening ? ' opening' : ''}`}
      role="button"
      tabIndex={0}
      aria-label={hint}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          open()
        }
      }}
      data-testid="envelope-intro"
    >
      <div className="inv-env">
        <div className="inv-env-back" />
        <div className={`inv-env-letter${photo ? ' has-photo' : ''}`}>
          {photo && (
            <img
              className="inv-env-letter-photo"
              src={photo}
              alt=""
              draggable={false}
            />
          )}
          <span className="inv-env-letter-kicker">{kicker}</span>
          <span className="inv-env-letter-title">{title}</span>
          <span className="inv-env-letter-date">{dateLine}</span>
        </div>
        <div className="inv-env-side l" />
        <div className="inv-env-side r" />
        <div className="inv-env-pocket" />
        <div className="inv-env-flap" />
        <div className="inv-env-seal">
          {seal ? (
            <WaxSeal motif={seal.motif} color={seal.color} size="100%" shadow={false} />
          ) : (
            <img src={sealImg} alt="" draggable={false} />
          )}
        </div>
      </div>
      <div className="inv-env-hint">{hint}</div>
    </div>
  )
}

// Google Places stores the venue as "<Georgian name>, <plus-code>, <English
// address>" in one string — pick out only the segments matching the viewer's
// language (and drop the plus-code). Falls back to the raw string if the
// filter leaves nothing (e.g. a hand-typed single-language address).
const GEORGIAN_RE = /[Ⴀ-ჿ]/
const PLUS_CODE_RE = /^[A-Z0-9]{4,}\+[A-Z0-9]+$/
const localizePlace = (location, lang) => {
  if (!location) return ''
  const parts = location.split(',').map((p) => p.trim()).filter(Boolean)
  const wanted = parts.filter(
    (p) => !PLUS_CODE_RE.test(p) && (lang === 'ka' ? GEORGIAN_RE.test(p) : !GEORGIAN_RE.test(p)),
  )
  return wanted.length ? wanted.join(', ') : location
}

const CountdownCells = ({ value, labels, className }) => (
  <div className={className}>
    {(['d', 'h', 'm', 's'] as const).map((k) => (
      <div className="inv-cell" key={k}>
        {/* key remounts the digit on change so the pop animation replays */}
        <b key={value[k]}>{value[k]}</b>
        <span>{labels[k]}</span>
      </div>
    ))}
  </div>
)

// Eased scroll that also works where rAF is throttled (embedded previews).
const scrollAnimTo = (targetY, after?: () => void) => {
  const startY = window.pageYOffset
  const dur = 700
  const t0 = performance.now()
  const step = (nowTs) => {
    const p = Math.min(1, (nowTs - t0) / dur)
    const ease = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2
    window.scrollTo(0, startY + (targetY - startY) * ease)
    if (p < 1) requestAnimationFrame(step)
  }
  requestAnimationFrame(step)
  setTimeout(() => {
    if (Math.abs(window.pageYOffset - targetY) > 2) window.scrollTo(0, targetY)
    if (after) after()
  }, dur + 80)
}

// ---------------------------------------------------------------------------
// Template preview (/templates/:templateId). The create/edit wizard iframes
// this route once per template so the picker shows the real invitation wearing
// each look, rather than a thumbnail that goes stale the moment a design
// changes. No event exists yet at that point, so the page runs off a stand-in
// and skips every network call.
// ---------------------------------------------------------------------------
// The stand-in's cover: the host's own photo, passed by the Design step as
// ?cover=. Only a URL or a leading-slash path is honoured; a relative path is
// resolved by the page exactly as a real event's would be (resolveImageUrl
// prefixes the API host), so a local upload and a stock URL both land.
const previewCoverFrom = (q: URLSearchParams) => {
  const raw = (q.get('cover') || '').trim()
  if (!raw) return null
  if (/^https?:\/\//i.test(raw) || raw.startsWith('/')) return raw
  return null
}

// The host's own words, when the Design step has them — so the preview shows
// their title fitting the design, not a sample's.
const previewWordFrom = (q: URLSearchParams, key: string) => (q.get(key) || '').trim().slice(0, 160) || null

const demoEventFor = (templateId, lang = 'en') => {
  const d = new Date()
  d.setMonth(d.getMonth() + 2)
  const date = d.toISOString().slice(0, 10)
  // The Design step passes the host's own overrides through the query string,
  // so the preview shows the colours and type they just picked — not a stock
  // render of the template.
  const q = new URLSearchParams(window.location.search)
  const hex = (v) => (/^#?[0-9a-f]{3}([0-9a-f]{3})?$/i.test(v || '') ? `#${v.replace(/^#/, '')}` : null)
  // Sample copy follows the template's category, so a wedding template
  // previews a wedding and a corporate one a gala.
  const sample = sampleEventFor(getTemplate(templateId).category, lang)
  const itineraryTimes = ['19:00', '20:00', '22:00']
  const own = (key: string) => previewWordFrom(q, key)
  const ownDate = /^\d{4}-\d{2}-\d{2}$/.test(own('date') || '') ? own('date') : null
  const ownTime = /^\d{2}:\d{2}$/.test(own('time') || '') ? own('time') : null
  return {
    event_id: 'template-preview',
    invite_template: templateId,
    accent_color: hex(q.get('accent')),
    background_color: hex(q.get('bg')),
    invite_font: q.get('font') || null,
    event_type: sample.event_type,
    language: lang,
    title: own('title') || sample.title,
    honoree_name: own('title') ? own('honoree') : sample.honoree_name,
    host_name: sample.host_name,
    date: ownDate || date,
    time: ownTime || sample.itinerary[0]?.time || '19:00',
    end_time: '23:00',
    location: own('location') || sample.location,
    description: sample.description,
    dress_code: sample.dress_code,
    arrival_instructions: '',
    itinerary: sample.itinerary.map((row, i) => ({
      time: row.time || itineraryTimes[i],
      title: row.title,
      where: row.where,
    })),
    wishlist_items: [],
    show_gifts: false,
    allow_plus_one: true,
    allow_guest_sharing: false,
    rsvp_closed: false,
    rsvp_invite_only: false,
    rsvp_deadline: null,
    capacity_remaining: null,
    cover_image: previewCoverFrom(q),
    latitude: null,
    longitude: null,
  }
}

const InvitationPage = () => {
  const { inviteLink, templateId } = useParams()
  // Set on the preview route only; drives the stand-in event above. ?lang=ka
  // lets the wizard preview the look with Georgian copy in it.
  const previewTemplate = templateId ? cleanTemplateId(templateId) : null
  const previewLang =
    new URLSearchParams(window.location.search).get('lang') === 'ka' ? 'ka' : 'en'
  const [event, setEvent] = useState(null)
  const [loading, setLoading] = useState(true)
  const [rsvpStatus, setRsvpStatus] = useState(null)
  const [rsvpForm, setRsvpForm] = useState({ name: '', message: '' })
  const [selectedStatus, setSelectedStatus] = useState('attending')
  const [plusOnes, setPlusOnes] = useState(0)
  const [submitting, setSubmitting] = useState(false)
  const [showContributeDialog, setShowContributeDialog] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const [contributionAmount, setContributionAmount] = useState('')
  const [contributorName, setContributorName] = useState('')
  const [contributionSuccess, setContributionSuccess] = useState(false)
  const [showShareDialog, setShowShareDialog] = useState(false)
  const [showToTop, setShowToTop] = useState(false)
  // Personal code for invite-only events. Pre-filled from ?code= so a host can
  // send each guest a link that already carries theirs.
  const [inviteCode, setInviteCode] = useState(() =>
    (new URLSearchParams(window.location.search).get('code') || '')
      .trim()
      .toUpperCase(),
  )
  // 'sandbox' records gifts instantly; a live gateway (flitt) redirects to a
  // hosted card page and confirms via callback.
  const [paymentProvider, setPaymentProvider] = useState('sandbox')
  const { open: openPaymentOverlay } = usePaymentOverlay()
  const nameInputRef = useRef(null)
  const rsvpScreenRef = useRef(null)

  // Envelope intro — skipped inside iframes (host-edit / live preview render
  // the invite directly), on payment-redirect returns and with ?noIntro=1.
  const [introDone, setIntroDone] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return (
      window.parent !== window || params.has('gift') || params.has('noIntro')
    )
  })

  // Guest-selected UI language overrides the event's default (interface only).
  // Bilingual events ('both') show the switcher and default to Georgian;
  // single-language events lock the interface to the event's language.
  const [langOverride, setLangOverride] = useState(null)
  const eventLang = event?.language || 'en'
  const isBilingual = eventLang === 'both'
  const lang = isBilingual ? langOverride || 'ka' : eventLang
  const t = translations[lang] || translations.en

  // Per-type looks: corporate gets the geometric navy-and-gold variant,
  // birthdays the blush botanical one; everything else keeps the default.
  const typeVariant =
    event?.event_type === 'Corporate Event' ||
    event?.event_type === 'კორპორატიული ღონისძიება'
      ? 'corporate'
      : event?.event_type === 'Birthday Party' || event?.event_type === 'დაბადების დღე'
        ? 'birthday'
        : event?.event_type === 'Wedding' || event?.event_type === 'ქორწილი'
          ? 'wedding'
          : event?.event_type === 'Graduation' || event?.event_type === 'გამოსაშვები'
            ? 'graduation'
            : event?.event_type === 'Baby Shower' || event?.event_type === 'ბეიბი შაუერი'
              ? 'babyshower'
              : 'default'
  // A template picked on the Design step wins; 'auto' keeps the per-type look.
  const eventTemplate = cleanTemplateId(event?.invite_template)
  const template = getTemplate(eventTemplate)
  const variant = eventTemplate === 'auto' ? typeVariant : eventTemplate
  useEffect(() => {
    loadTemplateFonts(eventTemplate)
  }, [eventTemplate])
  // The template's own look — inline palette and layout classes for the
  // data-driven designs, layout classes only for the five originals (their
  // CSS owns their palette). The host's overrides are merged on top below.
  const templateLook = useMemo(
    () => (eventTemplate === 'auto' ? { style: {}, className: '' } : buildTemplateLook(template)),
    [eventTemplate, template],
  )
  // Host overrides on top of the template: accent colour, background colour
  // and font pairing, each optional and each repainting the whole design.
  // Empty fields leave the template exactly as designed.
  // A painted template draws its first screen as a card, which takes the
  // host's colour for its words and — where its background is a flat paper
  // rather than artwork — for that too (resolvePaintedChoice); the rest of
  // the page follows what the card actually shows.
  const painted = eventTemplate !== 'auto' ? template.painted || null : null
  const paintedChoice = useMemo(
    () => (painted ? resolvePaintedChoice(painted, event?.accent_color, event?.background_color) : null),
    [painted, event?.accent_color, event?.background_color],
  )
  const look = useMemo(() => {
    const overrides =
      painted && paintedChoice
        ? paintedPageOverrides(painted, paintedChoice)
        : { accent: event?.accent_color, background: event?.background_color }
    return buildInviteLook({
      accent: overrides.accent,
      background: overrides.background,
      font: event?.invite_font,
      templateAccent: template.defaults.accent,
      templatePaper: template.legacy ? null : template.palette.paper,
    })
  }, [event?.accent_color, event?.background_color, event?.invite_font, template, painted, paintedChoice])
  useEffect(() => {
    loadInviteFont(event?.invite_font)
  }, [event?.invite_font])
  const dataDriven = eventTemplate !== 'auto' && !template.legacy
  const invClass = `inv${variant !== 'default' ? ` inv-${variant}` : ''}${lang === 'ka' ? ' inv-ka' : ''}${
    templateLook.className ? ` ${templateLook.className}` : ''
  }${dataDriven ? ' inv-data' : ''}${look.className ? ` ${look.className}` : ''}`
  // Host overrides come last so they win over the template's inline palette.
  const invStyle = {
    ...templateLook.style,
    ...(dataDriven ? { '--inv-where': `'${getMotif(template.motif).where}'` } : {}),
    ...look.style,
  } as any
  const kit = kitFor(variant, template)
  const Motif = kit.Corner || (dataDriven ? motifCornerFor(template) : TplCornerMotif)
  // The poster family sets the date as the design's big numeric mark.
  const posterLayout = eventTemplate !== 'auto' && template.layout === 'poster'
  // Set only for templates built around their own photograph (see
  // TEMPLATE_POSTER); the host's cover photo then shows on the RSVP card.
  const templatePoster = TEMPLATE_POSTER[variant] || null
  const sealImg =
    TEMPLATE_SEALS[variant] || (eventTemplate !== 'auto' && SEALS_BY_COLOR[template.seal]) || waxSeal
  // Data-driven templates press their own motif into wax of their accent
  // colour (components/invite/WaxSeal); the originals and the per-type looks
  // keep their photographed seals.
  const drawnSeal =
    eventTemplate !== 'auto' && !template.legacy
      ? { motif: template.motif, color: template.palette.a1 }
      : null

  // Bilingual events store Georgian in the base fields and English in the
  // *_en variants; pick per the guest's language, falling back to the base
  // text when no English version was provided.
  const bi = (base, en) =>
    isBilingual && lang === 'en' ? (en || '').trim() || base : base
  const evTitle = event ? bi(event.title, event.title_en) : ''
  const evDescription = event ? bi(event.description, event.description_en) : ''

  // Host edit mode — active only when the invite is embedded in the host's
  // event-page preview iframe with ?hostEdit=1. Key elements (cover, title,
  // date, place, time, description) become clickable and report the tapped
  // field to the parent page, which opens the matching editor.
  const isHostEdit = useMemo(
    () =>
      new URLSearchParams(window.location.search).get('hostEdit') === '1' &&
      window.parent !== window,
    [],
  )
  const hostEditProps = (field) =>
    isHostEdit
      ? {
          onClick: (e) => {
            e.preventDefault()
            e.stopPropagation()
            window.parent.postMessage(
              { type: 'storyfy-edit', field },
              window.location.origin,
            )
          },
          role: 'button',
          tabIndex: 0,
          // Shown as a floating hover badge via .inv-editable::after.
          'data-edit-label':
            lang === 'ka' ? 'დააჭირე შესაცვლელად' : 'Click to change',
        }
      : {}
  const hostEditClass = isHostEdit ? ' inv-editable' : ''

  const rsvpLabels = {
    attending: t.rsvpAttend,
    maybe: bi(event?.rsvp_label_maybe, event?.rsvp_label_maybe_en)?.trim() || t.rsvpMaybe,
    declined: bi(event?.rsvp_label_decline, event?.rsvp_label_decline_en)?.trim() || t.rsvpDecline,
  }
  const countLabels = countdownLabels(lang)

  const canShare = event?.allow_guest_sharing === true

  // PNG covers are typically transparent artwork that sits loose inside the
  // full-width framed backdrop; any other format is a regular photo whose
  // frame should shrink-wrap the image with no leftover backdrop beside it.
  const coverIsPng = (event?.cover_image || '')
    .split('?')[0]
    .toLowerCase()
    .endsWith('.png')

  const fetchEvent = useCallback(async () => {
    // Template preview: no event to fetch, just show the stand-in wearing the
    // requested look.
    if (previewTemplate) {
      setEvent(demoEventFor(previewTemplate, previewLang))
      setLoading(false)
      return
    }
    try {
      const response = await axios.get(`${API}/public/event/${inviteLink}`)
      setEvent(response.data)
    } catch (error) {
      toast.error(t.invitationNotFound)
    } finally {
      setLoading(false)
    }
  }, [inviteLink, previewTemplate, previewLang, t.invitationNotFound])

  useEffect(() => {
    fetchEvent()
  }, [fetchEvent])

  // Name the browser tab after the event.
  useEffect(() => {
    if (!evTitle) return
    const prev = document.title
    document.title = evTitle
    return () => {
      document.title = prev
    }
  }, [evTitle])

  // Hide the browser scrollbar while this page is mounted.
  useEffect(() => {
    document.documentElement.classList.add('inv-no-scrollbar')
    return () => document.documentElement.classList.remove('inv-no-scrollbar')
  }, [])

  // While the envelope covers the page, keep the invite from scrolling
  // underneath it.
  const showIntro = !introDone && !loading && !!event
  useEffect(() => {
    if (!showIntro) return
    const el = document.documentElement
    el.style.overflow = 'hidden'
    return () => {
      el.style.overflow = ''
    }
  }, [showIntro])

  // Scroll-up button visibility.
  useEffect(() => {
    const onScroll = () => setShowToTop(window.pageYOffset > 200)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Learn which payment provider is active so we show the right gift copy/flow.
  useEffect(() => {
    if (previewTemplate) return
    axios
      .get(`${API}/billing/config`)
      .then((res) => setPaymentProvider(res.data?.provider || 'sandbox'))
      .catch(() => {})
  }, [previewTemplate])

  // When the guest returns from the hosted card page (?gift=<payment_id>), poll
  // the (callback-updated) payment status and report the outcome.
  useEffect(() => {
    const giftId = new URLSearchParams(window.location.search).get('gift')
    if (!giftId) return
    let cancelled = false

    const finish = () => {
      if (!cancelled) window.history.replaceState({}, '', window.location.pathname)
    }
    const check = async (attempt = 0) => {
      try {
        const { data } = await axios.get(`${API}/public/payments/${giftId}/status`)
        if (cancelled) return
        if (data.status === 'paid') {
          toast.success(
            lang === 'ka'
              ? 'გადახდა წარმატებულია! მადლობა საჩუქრისთვის.'
              : 'Payment successful! Thank you for your gift.',
          )
          fetchEvent()
        } else if (data.status === 'pending' && attempt < 5) {
          setTimeout(() => check(attempt + 1), 2000)
          return
        } else if (data.status === 'pending') {
          toast.message(
            lang === 'ka' ? 'გადახდა მუშავდება...' : 'Your payment is being processed…',
          )
        } else {
          toast.error(
            lang === 'ka' ? 'გადახდა ვერ დასრულდა' : 'Payment was not completed',
          )
        }
      } catch (e) {
        /* ignore — leave the page as-is */
      }
      finish()
    }
    check()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const scrollToRsvp = () => {
    const el = rsvpScreenRef.current
    if (!el) return
    scrollAnimTo(window.pageYOffset + el.getBoundingClientRect().top, () => {
      nameInputRef.current?.focus({ preventScroll: true })
      setShowToTop(window.pageYOffset > 200)
    })
  }

  // Scrolls to the countdown + day-schedule column. Used by the poster
  // templates, whose first screen is the poster alone with everything else
  // below the fold.
  const detailsRef = useRef(null)
  const scrollToDetails = () => {
    const el = detailsRef.current
    if (!el) return
    scrollAnimTo(window.pageYOffset + el.getBoundingClientRect().top - 24, () => {
      setShowToTop(window.pageYOffset > 200)
    })
  }

  const handleSubmitRsvp = async () => {
    // Nothing to answer on the template preview — there is no event yet.
    if (previewTemplate) {
      toast.message(
        lang === 'ka'
          ? 'ეს შაბლონის გადახედვაა — პასუხი არ იგზავნება.'
          : 'This is a template preview — nothing is sent.',
      )
      return
    }
    if (!rsvpForm.name.trim()) {
      toast.error(lang === 'ka' ? 'შეიყვანე სახელი' : 'Please enter your name')
      return
    }
    if (event?.rsvp_invite_only && !inviteCode.trim()) {
      toast.error(t.inviteCodeRequired)
      return
    }
    setSubmitting(true)
    // Companions only make sense for an attending RSVP.
    const companions = selectedStatus === 'attending' ? plusOnes : 0
    try {
      await axios.post(`${API}/public/rsvp/${inviteLink}`, {
        name: rsvpForm.name,
        plus_ones: companions,
        // Legacy boolean kept so older API consumers keep working.
        plus_one: companions > 0,
        message: rsvpForm.message,
        status: selectedStatus,
        ...(event?.rsvp_invite_only ? { invite_code: inviteCode.trim() } : {}),
      })
      setRsvpStatus(selectedStatus)
      toast.success(t.thanksForResponding)
      // Guide the guest onward to the gift wishlist (if the event has one)
      // once the card has collapsed into the thanks note.
      if (event.show_gifts !== false && event.wishlist_items?.length) {
        setTimeout(() => {
          const gifts = document.querySelector('.inv-gifts-screen')
          if (gifts) scrollAnimTo(window.pageYOffset + gifts.getBoundingClientRect().top - 8)
        }, 250)
      }
    } catch (error) {
      const detail = error.response?.data?.detail
      toast.error(typeof detail === 'string' ? detail : t.errorOccurred)
    } finally {
      setSubmitting(false)
    }
  }

  const handleContribute = async () => {
    // !(x > 0) also rejects NaN (e.g. a lone "." from the sanitized input).
    if (!contributionAmount || !(parseFloat(contributionAmount) > 0)) {
      toast.error(
        lang === 'ka' ? 'შეიყვანე სწორი თანხა' : 'Please enter a valid amount',
      )
      return
    }
    if (parseFloat(contributionAmount) > 1000000) {
      toast.error(
        lang === 'ka' ? 'მაქსიმალური თანხაა 1,000,000' : 'Maximum amount is 1,000,000',
      )
      return
    }

    setSubmitting(true)
    try {
      const { data } = await axios.post(
        `${API}/public/contribute/${selectedItem.item_id}/checkout`,
        {
          amount: parseFloat(contributionAmount),
          contributor_name: contributorName || t.anonymous,
        },
      )
      if (data.checkout_url) {
        setShowContributeDialog(false)
        openPaymentOverlay(data.checkout_url)
        pollPaymentStatus({
          url: `${API}/public/payments/${data.payment_id}/status`,
          isDone: (d) => d.status === 'paid',
          onDone: () => {
            toast.success(
              lang === 'ka'
                ? 'გადახდა წარმატებულია! მადლობა საჩუქრისთვის.'
                : 'Payment successful! Thank you for your gift.',
            )
            fetchEvent()
          },
        })
      } else {
        setContributionSuccess(true)
        fetchEvent()
      }
    } catch (error) {
      const detail = error.response?.data?.detail
      toast.error(typeof detail === 'string' ? detail : t.errorOccurred)
    } finally {
      setSubmitting(false)
    }
  }

  // What this gift still needs. Drives both the pre-filled amount and the hint
  // under the input; recomputed from the freshly fetched event after every
  // cleared payment, so the suggestion shrinks as the gift fills up.
  const remainingFor = (item) =>
    Math.max(0, Math.round(((item?.target_amount || 0) - (item?.collected_amount || 0)) * 100) / 100)

  const openContributeDialog = (item) => {
    setSelectedItem(item)
    // Pre-fill with what's left so the common case is a single tap. A fully
    // funded item starts blank instead — over-funding stays possible, it just
    // shouldn't be what we suggest.
    const remaining = remainingFor(item)
    setContributionAmount(remaining > 0 ? String(remaining) : '')
    setContributorName('')
    setContributionSuccess(false)
    setShowContributeDialog(true)
  }

  const getInviteMessage = () =>
    lang === 'ka'
      ? `გიწვევ "${bi(event.title, event.title_en)}"-ზე!\n\n📅 ${formatDate(event.date, lang)}\n⏰ ${event.time}\n📍 ${event.location}\n\nდაადასტურე აქ: ${window.location.href}`
      : `You're invited to "${bi(event.title, event.title_en)}"!\n\n📅 ${formatDate(event.date, lang)}\n⏰ ${event.time}\n📍 ${event.location}\n\nRSVP here: ${window.location.href}`

  const copyText = (text, successMsg) => {
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success(successMsg))
      .catch(() => {
        const textArea = document.createElement('textarea')
        textArea.value = text
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        toast.success(successMsg)
      })
  }

  const shareViaWhatsApp = () => {
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(getInviteMessage())}`
    const newWindow = window.open(whatsappUrl, '_blank', 'noopener,noreferrer')
    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
      window.location.href = whatsappUrl
    }
  }

  const openGoogleMaps = () => {
    if (event.latitude && event.longitude) {
      window.open(`https://www.google.com/maps?q=${event.latitude},${event.longitude}`, '_blank')
    } else {
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`,
        '_blank',
      )
    }
  }

  // Event start/end as absolute instants (Georgia wall-clock, fixed UTC+4).
  const getEventDates = () => {
    const at = (date, time) =>
      new Date(`${date}T${(time || '00:00').slice(0, 5)}:00${EVENT_TZ_OFFSET}`)
    const start = at(event.date, event.time)
    const end =
      event.end_date || event.end_time
        ? at(event.end_date || event.date, event.end_time || event.time)
        : new Date(start.getTime() + 2 * 60 * 60 * 1000)
    return { start, end }
  }

  const toICSStamp = (date) => date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z'

  const addToGoogleCalendar = () => {
    const { start, end } = getEventDates()
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: bi(event.title, event.title_en) || '',
      dates: `${toICSStamp(start)}/${toICSStamp(end)}`,
      location: event.location || '',
      details: bi(event.description, event.description_en) || '',
      ctz: EVENT_TZID,
    })
    window.open(
      `https://calendar.google.com/calendar/render?${params.toString()}`,
      '_blank',
      'noopener,noreferrer',
    )
  }

  const downloadICS = () => {
    const { start, end } = getEventDates()
    const esc = (s) =>
      String(s || '')
        .replace(/\\/g, '\\\\')
        .replace(/;/g, '\\;')
        .replace(/,/g, '\\,')
        .replace(/\r?\n/g, '\\n')
    const uid = `${event.event_id || inviteLink || 'event'}@storyfy.ge`
    const ics = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Storyfy//Invitation//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `UID:${uid}`,
      `DTSTAMP:${toICSStamp(new Date())}`,
      `DTSTART:${toICSStamp(start)}`,
      `DTEND:${toICSStamp(end)}`,
      `SUMMARY:${esc(bi(event.title, event.title_en))}`,
      `LOCATION:${esc(event.location)}`,
      `DESCRIPTION:${esc(bi(event.description, event.description_en))}`,
      'END:VEVENT',
      'END:VCALENDAR',
    ].join('\r\n')

    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${event.title.replace(/\s+/g, '_')}.ics`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  // Countdown target — must be called unconditionally (hooks rule); harmless 0
  // while the event is still loading.
  const countdownTarget = useMemo(
    () => eventStartMs(event?.date, event?.time),
    [event?.date, event?.time],
  )
  const countdown = useCountdown(countdownTarget)

  // Countdown to the RSVP deadline (end of that day, event timezone); 0 when
  // the event has no deadline — the card then skips the whole block.
  const deadlineTarget = useMemo(() => {
    if (!event?.rsvp_deadline) return 0
    return new Date(`${event.rsvp_deadline.slice(0, 10)}T23:59:59${EVENT_TZ_OFFSET}`).getTime()
  }, [event?.rsvp_deadline])
  const deadlineCountdown = useCountdown(deadlineTarget)

  // ---- language toggle (design-styled pills) — only for bilingual events ----
  const LangPills = () =>
    isBilingual && (
    <div className="inv-lang">
      <button className={lang === 'ka' ? 'on' : ''} onClick={() => setLangOverride('ka')}>
        ქარ
      </button>
      <button className={lang === 'en' ? 'on' : ''} onClick={() => setLangOverride('en')}>
        ENG
      </button>
    </div>
  )

  if (loading) {
    // No loading screen — a plain white page until the event arrives, so no
    // default-palette background flashes before the event's own colors load.
    return <div className={`inv inv-loading${!introDone ? ' inv-covered' : ''}`} />
  }

  if (!event) {
    return (
      <div className={invClass} style={invStyle}>
        <DecorLayer />
        <LangPills />
        <div className="inv-center-screen">
          <div className="inv-card">
            <div className="inv-card-inner" style={{ padding: '20px 0' }}>
              <h1 className="inv-title">{t.invitationNotFound}</h1>
              <p className="inv-sub">{t.invitationExpired}</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const giftCurrency = selectedItem?.currency || 'GEL'
  const giftSymbol = currencySymbol(giftCurrency)
  const rsvpDisabled =
    !rsvpForm.name.trim() ||
    submitting ||
    (event.rsvp_invite_only && !inviteCode.trim())
  const wishlistItems =
    event.show_gifts !== false && event.wishlist_items ? event.wishlist_items : []
  // With RSVP closed and no wishlist, screen 2 would be empty — skip it (and
  // the footer) entirely so the invite is a single non-scrolling screen.
  const hasBottom = !event.rsvp_closed || wishlistItems.length > 0

  // selectedItem is the snapshot taken when the dialog opened; a cleared gift
  // refetches the event, so re-resolve against the live list to keep the
  // "still needed" figure honest while the dialog stays open.
  const activeItem = selectedItem
    ? wishlistItems.find((i) => i.item_id === selectedItem.item_id) || selectedItem
    : null

  // Multi-step events carry their own day schedule; it replaces the generic
  // time row (honoree/dress/details rows still follow it).
  const itineraryRows = (event.itinerary || [])
    .filter((it) => it && it.title && it.start)
    .map((it, i) => ({
      key: `it${i}`,
      label: it.end ? `${it.start}–${it.end}` : it.start,
      what: bi(it.title, it.title_en),
      where: bi(it.location, it.location_en) || null,
    }))

  const detailRows = [
    ...(itineraryRows.length
      ? itineraryRows
      : [
          {
            key: 'time',
            label: t.time,
            what: formatTimeRange(event.time, event.end_time),
            where: localizePlace(event.location, lang),
          },
        ]),
    event.honoree_name && {
      key: 'honoree',
      label: t.guestOfHonor,
      what: bi(event.honoree_name, event.honoree_name_en),
      where: null,
    },
    event.dress_code && {
      key: 'dress',
      label: lang === 'ka' ? 'დრეს-კოდი' : 'Dress code',
      what: bi(event.dress_code, event.dress_code_en),
      where: null,
    },
    event.arrival_instructions && {
      key: 'arrival',
      label: lang === 'ka' ? 'დეტალები' : 'Details',
      what: bi(event.arrival_instructions, event.arrival_instructions_en),
      where: null,
    },
  ].filter(Boolean)

  return (
    <div className={`${invClass}${showIntro ? ' inv-covered' : ''}`} style={invStyle}>
      {showIntro && (
        <EnvelopeIntro
          key="env"
          sealImg={sealImg}
          seal={drawnSeal}
          photo={event.cover_image && !painted ? resolveImageUrl(event.cover_image) : null}
          kicker={t.youreInvited}
          title={evTitle}
          dateLine={formatDateRange(event.date, event.end_date, lang)}
          hint={lang === 'ka' ? 'შეეხე ბეჭედს გასახსნელად' : 'Tap the seal to open'}
          onDone={() => setIntroDone(true)}
        />
      )}
      {!painted && <DecorLayer variant={variant} template={eventTemplate === 'auto' ? null : template} />}
      <LangPills />
      {canShare && (
        <button className="inv-share" onClick={() => setShowShareDialog(true)} data-testid="share-btn">
          <Share2 style={{ width: 16, height: 16 }} />
        </button>
      )}

      {/* ---------------- screen 1: the invitation ---------------- */}
      <div className="inv-screen1">
        <div className="inv-main">
          <div className="inv-id">
            {painted ? (
              <div className={`inv-painted-card${hostEditClass}`} {...hostEditProps('title')}>
                <PaintedCard
                  def={painted}
                  choice={paintedChoice}
                  words={{
                    title: evTitle,
                    honoree: bi(event.honoree_name, event.honoree_name_en),
                    date: event.date,
                    time: event.time,
                    location: localizePlace(event.location, lang),
                  }}
                  lang={lang}
                  font={event.invite_font}
                  play={introDone}
                />
              </div>
            ) : (
            <>
            <h1
              className={`inv-title inv-rise inv-e2${hostEditClass}`}
              {...hostEditProps('title')}
            >
              {evTitle}
            </h1>
            <div className="inv-sub inv-rise inv-e3">{t.youreInvited}</div>

            <div className={`inv-photo-wrap inv-rise${!templatePoster && event.cover_image && !coverIsPng ? ' inv-wrap-fit' : ''}`}>
              <div
                className={`inv-ph${templatePoster ? ' inv-ph-poster' : ''}${!templatePoster && event.cover_image ? ' has-img' : ''}${!templatePoster && event.cover_image && !coverIsPng ? ' inv-ph-fit' : ''}${templatePoster ? '' : hostEditClass}`}
                {...(templatePoster ? {} : hostEditProps('cover'))}
              >
                {templatePoster ? (
                  <>
                    <img src={templatePoster} alt="" draggable={false} />
                    {/* The flowers, cut out of the photo into an alpha sprite
                        and floated over their own pixels — the mockup's
                        drifting-garnish animation. */}
                    {variant === 'cocktail' && (
                      <img
                        className="inv-garnish"
                        src={`${TPL_ART}/cocktail-garnish.webp`}
                        alt=""
                        draggable={false}
                      />
                    )}
                  </>
                ) : event.cover_image ? (
                  <img src={resolveImageUrl(event.cover_image)} alt="" />
                ) : (
                  /* No photo: the template's own motif fills the frame, so an
                     invitation without one still looks finished. */
                  <Motif className="inv-ph-mark" />
                )}
              </div>
              <div className="inv-seal">
                {drawnSeal ? (
                  <WaxSeal motif={drawnSeal.motif} color={drawnSeal.color} size="100%" shadow={false} />
                ) : (
                  <img src={sealImg} alt="" draggable={false} />
                )}
              </div>
            </div>

            <div
              className={`inv-when inv-rise inv-e5${hostEditClass}`}
              {...hostEditProps('date')}
            >
              {posterLayout
                ? // The poster layouts set the date as the design's big
                  // numeric mark ("10/11"); the full date stays in the
                  // schedule card below.
                  event.date.slice(8, 10) + '/' + event.date.slice(5, 7)
                : formatDateRange(event.date, event.end_date, lang)}
            </div>
            <div
              className={`inv-place inv-rise inv-e5${hostEditClass}`}
              {...hostEditProps('location')}
            >
              <svg className="inv-place-pin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                <path d="M12 21s7-5.6 7-11a7 7 0 10-14 0c0 5.4 7 11 7 11z" />
                <circle cx="12" cy="10" r="2.5" />
              </svg>
              {localizePlace(event.location, lang)}
            </div>
            </>
            )}

            <div className="inv-mini inv-rise inv-e6">
              <div className="inv-btnrow">
                <button className="inv-btn" onClick={openGoogleMaps} data-testid="open-maps-btn">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#41544c" strokeWidth="1.6">
                    <path d="M12 21s7-5.6 7-11a7 7 0 10-14 0c0 5.4 7 11 7 11z" />
                    <circle cx="12" cy="10" r="2.5" />
                  </svg>
                  <span>{t.openInGoogleMaps}</span>
                </button>
                {/* Poster templates fill the first screen with the poster, so
                    they carry a way down to the schedule — the same solid
                    pill as the RSVP button. */}
                {(templatePoster || posterLayout || painted) && (
                  <button className="inv-rsvp-btn" onClick={scrollToDetails} data-testid="details-open-btn">
                    {lang === 'ka' ? 'დეტალები' : 'Details'}
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="inv-info" ref={detailsRef}>
            <CountdownCells value={countdown} labels={countLabels} className="inv-count inv-rise inv-e4" />

            {/* Heading, date and rows are one group so a template can draw a
                single card around them. Default looks keep the flat column:
                .inv-sched is display:contents unless a template restyles it. */}
            <div className="inv-sched">
            <div className="inv-leafrule inv-rise inv-e5">
              <span className="rule" />
              <h2 className="inv-h2" style={{ fontFamily: "'YAHCDon1F9s_0', var(--inv-serif)" }}>
                {lang === 'ka' ? 'დღის განრიგი' : 'Day Schedule'}
              </h2>
              <span className="rule" />
            </div>
            <div className="inv-h2sub inv-rise inv-e5">
              {formatDate(event.date, lang)}
            </div>

            <div className="inv-rows inv-rise inv-e6">
              {detailRows.map((row) => {
                // Which event field each schedule row edits in host-edit mode.
                const rowField = {
                  time: 'time',
                  honoree: 'honoree_name',
                  dress: 'dress_code',
                  arrival: 'arrival_instructions',
                }[row.key]
                return (
                <div
                  className={`inv-row${rowField ? hostEditClass : ''}`}
                  key={row.key}
                  {...(rowField ? hostEditProps(rowField) : {})}
                >
                  <span className="t">{row.label}</span>
                  <span>
                    <span className="what">{row.what}</span>
                    {row.where && (
                      <>
                        <br />
                        <span className="where">{row.where}</span>
                      </>
                    )}
                  </span>
                </div>
                )
              })}
            </div>
            </div>

            {event.description && (
              <div
                className={`inv-desc inv-rise inv-e6${hostEditClass}`}
                {...hostEditProps('description')}
              >
                {evDescription}
              </div>
            )}

            <div className="inv-mini inv-rise inv-e6">
              <div className="inv-mini-title">{t.addToCalendar}</div>
              <div className="inv-btnrow">
                <button className="inv-btn" onClick={addToGoogleCalendar} data-testid="add-google-calendar">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#41544c" strokeWidth="1.6">
                    <rect x="3" y="5" width="18" height="16" rx="2" />
                    <path d="M8 3v4M16 3v4M3 10h18" />
                  </svg>
                  <span>Google Calendar</span>
                </button>
                <button className="inv-btn" onClick={downloadICS} data-testid="add-apple-calendar">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#41544c" strokeWidth="1.6">
                    <rect x="3" y="5" width="18" height="16" rx="2" />
                    <path d="M8 3v4M16 3v4M3 10h18M12 13v5M9.5 15.5L12 18l2.5-2.5" />
                  </svg>
                  <span>Apple Calendar</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* RSVP band pinned to the bottom of the first screen; disappears
            entirely once the guest has responded — the thanks note lives in
            the RSVP card. */}
        {!rsvpStatus && (
        <div className="inv-rsvp-band">
          {event.rsvp_closed ? (
            <>
              <div className="inv-rsvp-q">{t.rsvpClosed}</div>
              <div className="inv-rsvp-note">{t.rsvpDeadlinePassed}</div>
            </>
          ) : (
            <>
              <div className="inv-rsvp-q">{t.willYouAttend}</div>
              <button className="inv-rsvp-btn" onClick={scrollToRsvp} data-testid="rsvp-open-btn">
                RSVP
              </button>
              {event.rsvp_deadline && (
                <div className="inv-rsvp-note">
                  {lang === 'ka'
                    ? `გთხოვთ, დაადასტუროთ დასწრება ${formatDate(event.rsvp_deadline, lang)}-მდე`
                    : `Please confirm by ${formatDate(event.rsvp_deadline, lang)}`}
                </div>
              )}
            </>
          )}
        </div>
        )}
      </div>

      {/* ---------------- screen 2: RSVP + gifts, centered together ---------------- */}
      {hasBottom && (
      <div className="inv-bottom">
      {/* Closed: say why rather than silently dropping the form — a guest who
          finds no RSVP anywhere assumes the invitation is broken. */}
      {event.rsvp_closed && (
        <section className="inv-rsvp-screen">
          <div className="inv-card">
            <Motif className="corner tl" />
            <Motif className="corner br" />
            <div className="inv-card-inner">
              <h1 className="inv-title">{t.rsvpClosedTitle}</h1>
              <div className="inv-thanks" data-testid="rsvp-closed-note">
                {event.rsvp_closed_reason === 'closed_by_host'
                  ? t.rsvpClosedByHost
                  : event.rsvp_closed_reason === 'full'
                    ? t.rsvpClosedFull
                    : t.rsvpClosedDeadline}
                {' ✶'}
              </div>
            </div>
          </div>
        </section>
      )}
      {!event.rsvp_closed && (
        <section className="inv-rsvp-screen" ref={rsvpScreenRef}>
          <div className="inv-card">
            <Motif className="corner tl" />
            <Motif className="corner br" />
            <div className="inv-card-inner">
              {/* Templates that own their poster keep screen 1 for the design;
                  the host's photo is shown here instead so it is still seen.
                  A painted design carries no photo at all. */}
              {templatePoster && event.cover_image && (
                <div
                  className={`inv-rsvp-photo${hostEditClass}`}
                  {...hostEditProps('cover')}
                >
                  <img src={resolveImageUrl(event.cover_image)} alt="" />
                </div>
              )}
              {rsvpStatus ? (
                <>
                  <h1 className="inv-title">{t.thanksForResponding}</h1>
                  <div className="inv-thanks">
                    {rsvpStatus === 'attending' && t.cantWaitToSeeYou}
                    {rsvpStatus === 'maybe' && t.hopeYouCanMakeIt}
                    {rsvpStatus === 'declined' && t.sorryYouCantMake}
                    {' ✶'}
                  </div>
                </>
              ) : (
                <>
                  <div className="inv-deadline-note">
                    {event.rsvp_deadline
                      ? lang === 'ka'
                        ? `გთხოვთ, დაადასტუროთ დასწრება ${formatDate(event.rsvp_deadline, lang)}-მდე`
                        : `Please confirm by ${formatDate(event.rsvp_deadline, lang)}`
                      : lang === 'ka'
                        ? 'გთხოვთ, დაადასტუროთ დასწრება'
                        : 'Please confirm your attendance'}
                  </div>
                  <CountdownCells
                    value={event.rsvp_deadline ? deadlineCountdown : countdown}
                    labels={countLabels}
                    className="inv-count2"
                  />

                  <div className="inv-leafrule">
                    <span className="rule" />
                    <h2 className="inv-h2">{t.willYouAttend}</h2>
                    <span className="rule" />
                  </div>

                  <input
                    className="inv-fld"
                    ref={nameInputRef}
                    type="text"
                    autoComplete="name"
                    spellCheck={false}
                    maxLength={120}
                    placeholder={t.enterYourName}
                    value={rsvpForm.name}
                    onChange={(e) => setRsvpForm({ ...rsvpForm, name: e.target.value })}
                    data-testid="rsvp-name-input"
                  />

                  {/* Invite-only events additionally require the personal code
                      the host sent (also accepted via ?code= on the link). */}
                  {event.rsvp_invite_only && (
                    <>
                      <div className="inv-qlabel">{t.yourInviteCode}</div>
                      <input
                        className="inv-fld"
                        type="text"
                        autoComplete="off"
                        spellCheck={false}
                        maxLength={24}
                        placeholder={t.inviteCodePlaceholder}
                        value={inviteCode}
                        onChange={(e) =>
                          setInviteCode(
                            e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''),
                          )
                        }
                        style={{ letterSpacing: '0.2em', textTransform: 'uppercase' }}
                        data-testid="rsvp-invite-code-input"
                      />
                    </>
                  )}

                  {/* Nudge when the event is nearly full, so a guest bringing
                      companions finds out before the submit is refused. */}
                  {typeof event.capacity_remaining === 'number' &&
                    event.capacity_remaining > 0 &&
                    event.capacity_remaining <= 10 && (
                      <div className="inv-deadline-note" data-testid="rsvp-places-left">
                        {event.capacity_remaining === 1
                          ? t.onePlaceLeft
                          : t.placesLeft.replace('{n}', String(event.capacity_remaining))}
                      </div>
                    )}

                  <div className="inv-qlabel">
                    {lang === 'ka' ? 'დასწრება' : 'Attendance'}
                  </div>
                  <div className="inv-choices" data-testid="rsvp-status-selector">
                    {(['attending', 'maybe', 'declined'] as const).map((s) => (
                      <button
                        key={s}
                        type="button"
                        className={`inv-choice${selectedStatus === s ? ' sel' : ''}`}
                        onClick={() => setSelectedStatus(s)}
                        data-testid={`rsvp-${s}-btn`}
                      >
                        {rsvpLabels[s]}
                      </button>
                    ))}
                  </div>

                  {selectedStatus === 'attending' && event.allow_plus_one !== false && (
                    <>
                      <div className="inv-qlabel">{t.plusOnesQuestion}</div>
                      <div className="inv-choices" data-testid="rsvp-plusones-selector">
                        {Array.from({ length: 2 }, (_, n) => (
                          <button
                            key={n}
                            type="button"
                            className={`inv-choice${plusOnes === n ? ' sel' : ''}`}
                            onClick={() => setPlusOnes(n)}
                            data-testid={`rsvp-plusones-${n}`}
                          >
                            {n === 0 ? t.plusOnesNone : `+${n}`}
                          </button>
                        ))}
                      </div>
                    </>
                  )}

                  <textarea
                    className="inv-fld"
                    spellCheck={false}
                    maxLength={600}
                    placeholder={t.messageToHost}
                    value={rsvpForm.message}
                    onChange={(e) => {
                      // Auto-grow with content (manual resize is disabled in CSS).
                      e.target.style.height = 'auto'
                      e.target.style.height = `${e.target.scrollHeight}px`
                      setRsvpForm({ ...rsvpForm, message: e.target.value })
                    }}
                    data-testid="rsvp-message-input"
                  />

                  <button
                    className="inv-send"
                    disabled={rsvpDisabled}
                    onClick={handleSubmitRsvp}
                    data-testid="rsvp-submit-btn"
                  >
                    {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                    {t.confirmRsvp}
                  </button>
                </>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ---------------- gifts ---------------- */}
      {wishlistItems.length > 0 && (
        <section className="inv-gifts-screen">
          <div className="inv-card">
            <Motif className="corner tl" />
            <Motif className="corner br" />
            <div className="inv-card-inner">
              <div className="inv-leafrule" style={{ marginTop: 0 }}>
                <span className="rule" />
                <h2 className="inv-h2">{t.wishlist}</h2>
                <span className="rule" />
              </div>
              {wishlistItems.map((item) => {
                const progress =
                  item.target_amount > 0
                    ? Math.min((item.collected_amount / item.target_amount) * 100, 100)
                    : 0
                const remaining = Math.max(0, item.target_amount - item.collected_amount)
                return (
                  <div className="inv-gift-item" key={item.item_id} data-testid={`wishlist-item-${item.item_id}`}>
                    <div className="inv-gift-head">
                      <span className="inv-gift-name">
                        {item.image_url ? (
                          <img
                            className="inv-gift-photo"
                            src={resolveImageUrl(item.image_url)}
                            alt=""
                            loading="lazy"
                            data-testid={`wishlist-item-photo-${item.item_id}`}
                          />
                        ) : (
                          <span>{wishlistEmoji(item.icon)}</span>
                        )}
                        {item.title}
                      </span>
                      {item.is_funded ? (
                        <span className="inv-gift-funded">
                          <Heart style={{ width: 12, height: 12, display: 'inline' }} /> {t.funded}
                        </span>
                      ) : (
                        <button
                          className="inv-gift-btn"
                          onClick={() => openContributeDialog(item)}
                          data-testid={`contribute-btn-${item.item_id}`}
                        >
                          {t.contribute}
                        </button>
                      )}
                    </div>
                    <div className="inv-gift-meta">
                      <span className="inv-progress">
                        <i style={{ width: `${progress}%` }} />
                      </span>
                      <span className="inv-gift-amount">
                        {item.is_funded
                          ? `${currencySymbol(item.currency)}${formatMoney(item.collected_amount)} / ${currencySymbol(item.currency)}${formatMoney(item.target_amount)}`
                          : `${currencySymbol(item.currency)}${formatMoney(remaining)} ${t.remaining}`}
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </section>
      )}
      </div>
      )}

      {hasBottom && <div className="inv-foot">{kit.foot}</div>}
      <button
        className={`inv-totop${showToTop ? ' show' : ''}`}
        onClick={() => scrollAnimTo(0, () => setShowToTop(false))}
        title="⬆"
      >
        ↑
      </button>

      {/* Contribution Dialog */}
      <Dialog open={showContributeDialog} onOpenChange={setShowContributeDialog}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
          {!contributionSuccess ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">
                  {t.contribute} - {selectedItem?.title}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4 pb-8">
                <div>
                  <Label className="text-[13px] font-medium text-[#15140F]/70 dark:text-gray-400 mb-1.5 block">
                    {t.contributorName}
                  </Label>
                  <Input
                    value={contributorName}
                    onChange={(e) => setContributorName(e.target.value)}
                    placeholder={t.anonymous}
                    maxLength={120}
                    className="h-12 rounded-xl"
                    data-testid="contributor-name-input"
                  />
                </div>

                <div>
                  <Label className="text-[13px] font-medium text-[#15140F]/70 dark:text-gray-400 mb-1.5 block">
                    {t.amount} *
                  </Label>
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={contributionAmount}
                        onChange={(e) => {
                          const v = e.target.value
                            .replace(/,/g, '.')
                            .replace(/[^0-9.]/g, '')
                            .replace(/(\..*)\./g, '$1')
                          setContributionAmount(v)
                        }}
                        placeholder="0"
                        maxLength={9}
                        className="h-12 rounded-xl text-lg pr-4 pl-4"
                        data-testid="contribution-amount-input"
                      />
                    </div>
                    <div
                      className="w-24 h-12 rounded-xl border border-input bg-gray-50 dark:bg-white/5 flex items-center justify-center gap-1 select-none"
                      data-testid="contribution-currency"
                    >
                      <span className="font-medium">{giftSymbol}</span>
                      <span className="text-gray-500 dark:text-gray-400">{giftCurrency}</span>
                    </div>
                  </div>
                  {/* Explain where the pre-filled number came from, and keep it
                      honest as gifts land — the remainder shrinks each time. */}
                  {activeItem && remainingFor(activeItem) > 0 && (
                    <p
                      className="text-xs text-[#8A877E] dark:text-gray-400 mt-2 tabular-nums"
                      data-testid="contribution-remaining-hint"
                    >
                      {lang === 'ka'
                        ? `დარჩენილია ${giftSymbol}${formatMoney(remainingFor(activeItem))} — თანხის შეცვლა შეგიძლია`
                        : `${giftSymbol}${formatMoney(remainingFor(activeItem))} still needed — you can change the amount`}
                    </p>
                  )}
                  <p className="text-xs text-[#8A877E] dark:text-gray-400 mt-2">
                    {paymentProvider === 'sandbox'
                      ? t.simulatedContribution
                      : lang === 'ka'
                        ? 'უსაფრთხო გადახდა ბარათით — გადამისამართდებით გადახდის გვერდზე.'
                        : "Secure card payment — you'll be redirected to our payment page."}
                  </p>
                </div>

                {contributionAmount && (
                  <div className="bg-[#15140F]/[0.03] border border-[#15140F]/10 dark:bg-white/5 dark:border-white/10 rounded-xl p-3 text-center">
                    <p className="text-sm text-[#8A877E] dark:text-gray-400">
                      {lang === 'ka' ? 'კონტრიბუციის თანხა' : 'Contribution Amount'}
                    </p>
                    <p className="text-2xl font-semibold text-[#15140F] dark:text-[#F3F0E9] font-display tracking-tight">
                      {giftSymbol}
                      {formatMoney(parseFloat(contributionAmount) || 0)}
                    </p>
                  </div>
                )}

                <div className="pt-2">
                  <Button
                    onClick={handleContribute}
                    disabled={submitting || !contributionAmount}
                    className="w-full h-12 text-white rounded-xl font-semibold"
                    style={{ backgroundColor: '#d8a48f' }}
                    data-testid="submit-contribution-btn"
                  >
                    {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : t.contribute}
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="py-8 text-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-500/15 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-2xl font-semibold text-[#15140F] dark:text-[#F3F0E9] font-display tracking-tight mb-2">
                {t.thankYouContribution}
              </h3>
              <p className="text-[#4B4A44] dark:text-gray-300 mb-2">{t.contributionRecorded}</p>
              <p className="text-2xl font-semibold text-[#15140F] dark:text-[#F3F0E9] font-display tracking-tight mb-6">
                {giftSymbol}
                {formatMoney(parseFloat(contributionAmount || '0'))}
              </p>
              <Button
                onClick={() => setShowContributeDialog(false)}
                className="rounded-xl px-8 text-white"
                style={{ backgroundColor: '#d8a48f' }}
              >
                {t.done}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      {canShare && (
        <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
          <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle className="font-display">{t.shareInvitation}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-4">
              <Button
                onClick={shareViaWhatsApp}
                className="w-full h-12 bg-green-500 text-white rounded-xl font-medium hover:bg-green-600 transition-all duration-200"
                data-testid="share-whatsapp-btn"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                {t.shareViaWhatsApp}
              </Button>
              <Button
                onClick={() => copyText(getInviteMessage(), t.inviteMessageCopied)}
                variant="outline"
                className="w-full h-12 rounded-xl font-medium bg-white dark:bg-[#1D1C17] border border-[#15140F]/12 dark:border-white/10 text-[#15140F] dark:text-[#F3F0E9] hover:bg-[#15140F]/[0.03] dark:hover:bg-white/5 transition-all duration-200"
                data-testid="copy-message-btn"
              >
                <Copy className="w-5 h-5 mr-2" />
                {t.copyInviteMessage}
              </Button>
              <Button
                onClick={() => copyText(window.location.href, t.inviteLinkCopied)}
                variant="outline"
                className="w-full h-12 rounded-xl font-medium bg-white dark:bg-[#1D1C17] border border-[#15140F]/12 dark:border-white/10 text-[#15140F] dark:text-[#F3F0E9] hover:bg-[#15140F]/[0.03] dark:hover:bg-white/5 transition-all duration-200"
                data-testid="copy-link-btn"
              >
                <Copy className="w-5 h-5 mr-2" />
                {t.copyLink}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}

export default InvitationPage
